import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRedo as iconRefresh,
  faChevronUp,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';

import Title from '../../components/Title';
import DraftDetailToggle from './components/DraftDetailToggle';
import DraftSettingsManager from './components/DraftSettingsManager';
import DraftOrderManager from './components/DraftOrderManager';
import DraftUserCards from './components/DraftUserCards';
import { auth } from '../../helpers/firebase';
import { toastError } from '../../helpers/toastHelpers';
import getDraftForAll from '../../data/getDraftForAll';
import getAllUsers from '../../data/getAllUsers';
import getImageUrl from '../../data/getImageUrl';
import { titleHelper } from '../../helpers/titleHelper';
import './admin.css';

const Admin = () => {
  const [users, setUsers] = useState({});
  const [draft, setDraft] = useState({});
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [isDraftSettingsMinimized, setIsDraftSettingsMinimized] = useState(true);
  const [isDraftOrderMinimized, setIsDraftOrderMinimized] = useState(true);
  const [isUserDetailsMinimized, setIsUserDetailsMinimized] = useState(false);

  const getAllUsersData = useCallback(async () => {
    setLoadingUsers(true);
    const usersData = await getAllUsers();

    if (usersData.error) {
      toastError(usersData.message);
      setUsers({});
    } else {
      setUsers(usersData.users);
    }

    setLoadingUsers(false);
  }, []);

  const getDraftData = useCallback(async () => {
    setLoadingDraft(true);
    const draftResponse = await getDraftForAll();

    if (draftResponse.error) {
      toastError(draftResponse.message);
      setDraft({});
      setLoadingDraft(false);
      return;
    }

    const nextDraft = { ...draftResponse.draft };

    // Enrich each draft entry with resolved logo URL and normalized song list.
    // eslint-disable-next-line no-restricted-syntax
    for (const [key] of Object.entries(nextDraft)) {
      if (nextDraft[key].logoUpload) {
        // eslint-disable-next-line no-await-in-loop
        nextDraft[key].logoUrl = await getImageUrl(nextDraft[key].logoUploadFilename);
      }

      nextDraft[key].songList = nextDraft[key].songList ? JSON.parse(nextDraft[key].songList) : [];
    }

    setDraft(nextDraft);
    setLoadingDraft(false);
  }, []);

  const refreshData = useCallback(() => {
    getDraftData();
    getAllUsersData();
  }, [getAllUsersData, getDraftData]);

  useEffect(() => {
    titleHelper('Draft (Admin)');
    if (auth.currentUser) {
      refreshData();
    }
  }, [refreshData]);

  return (
    <div id="psf-draft-admin-container" className="psf-container">
      <div className="container">
        <Title text="Draft Admin" />
      </div>

      <div className="container d-flex justify-content-end align-items-center mb-3">
        <Link to="/draft/admin/create" className="btn btn-sm btn-outline-success text-green-alt px-3 shadow mr-2">
          Draft Year Generator
        </Link>

        <button
          type="button"
          className="btn btn-sm btn-success px-3 shadow"
          onClick={refreshData}
        ><FontAwesomeIcon icon={iconRefresh} />
        </button>
      </div>

      <div className="container-fluid shadow row-background p-4 mb-3">
        <h4 className="mb-3">Draft Controls</h4>

        <div className="d-flex align-items-center">
          <DraftDetailToggle id="draftDisableCheck" draftDetailToToggle="disabled" draftDetailLabel="Draft Disabled" />
          <div className="ml-4">
            <DraftDetailToggle id="draftForceOpenCheck" draftDetailToToggle="forceOpen" draftDetailLabel="Force Draft Open" />
          </div>
        </div>
      </div>
      <div className="container-fluid shadow row-background p-4 mb-3 mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Draft Settings</h4>
          <button
            type="button"
            className="btn btn-sm btn-outline-light px-3 draft-section-toggle"
            onClick={() => setIsDraftSettingsMinimized(!isDraftSettingsMinimized)}
            aria-expanded={!isDraftSettingsMinimized}
            aria-label={isDraftSettingsMinimized ? 'Expand draft settings section' : 'Minimize draft settings section'}
          >
            <FontAwesomeIcon icon={isDraftSettingsMinimized ? faChevronDown : faChevronUp} className="mr-2" />
            {isDraftSettingsMinimized ? 'Expand' : 'Minimize'}
          </button>
        </div>

        {!isDraftSettingsMinimized && <DraftSettingsManager />}
      </div>

      <div className="container-fluid shadow row-background p-4 mb-3 mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Draft Order</h4>
          <button
            type="button"
            className="btn btn-sm btn-outline-light px-3 draft-section-toggle"
            onClick={() => setIsDraftOrderMinimized(!isDraftOrderMinimized)}
            aria-expanded={!isDraftOrderMinimized}
            aria-label={isDraftOrderMinimized ? 'Expand draft order section' : 'Minimize draft order section'}
          >
            <FontAwesomeIcon icon={isDraftOrderMinimized ? faChevronDown : faChevronUp} className="mr-2" />
            {isDraftOrderMinimized ? 'Expand' : 'Minimize'}
          </button>
        </div>

        {!isDraftOrderMinimized && <DraftOrderManager />}
      </div>

      <div className="container-fluid shadow row-background p-4 mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">User Draft Details</h4>
          <button
            type="button"
            className="btn btn-sm btn-outline-light px-3 draft-section-toggle"
            onClick={() => setIsUserDetailsMinimized(!isUserDetailsMinimized)}
            aria-expanded={!isUserDetailsMinimized}
            aria-label={isUserDetailsMinimized ? 'Expand user draft details section' : 'Minimize user draft details section'}
          >
            <FontAwesomeIcon icon={isUserDetailsMinimized ? faChevronDown : faChevronUp} className="mr-2" />
            {isUserDetailsMinimized ? 'Expand' : 'Minimize'}
          </button>
        </div>

        {!isUserDetailsMinimized && (
          <DraftUserCards users={users} draft={draft} loading={loadingUsers || loadingDraft} />
        )}
      </div>
    </div>
  );
};

export default Admin;
